import * as d3 from 'd3';
import _ from 'lodash';
import { store } from '../../configureStore';
import { decrementEvents, filterOnEvent, incrementEvents } from './actions';
import { MAX_EVENT_NO } from './DisplayEvents';
import { pathwayOffIcon, pathwayOnIcon } from './PathwayIcons';
import { centreWidth as centre, getDatasetTheme, getNoDataTheme, metricConfig, pathwayTheme } from './PathwayTheme';
import { bottomRoundedRect, drawLegend, drawMetricBoxHeader, drawMetricBoxHeaderText, drawMetrics, metricBox } from './SvgUtil';
import { CohortPathway, DestinationFilter, EventFilter, EventMetricWithConfig } from './types/CohortPathway';
import { destinationFilterDescription, DestinationMetric, eventFilterDescription, filterIsEmpty, hasData } from './types/EventMetric';

// https://www.d3-graph-gallery.com/intro_d3js.html
// https://medium.com/nightingale/d3-and-react-a-design-pattern-for-responsive-charts-f77337d37ab9
// https://medium.com/better-programming/reactive-charts-in-angular-8-using-d3-4550bb0b4255
// https://developer.mozilla.org/en-US/docs/Web/SVG/Element/rect
// https://codepen.io/ajv/pen/wKdrWb - rounded rect corners
// https://microsoft.github.io/chart-parts/
// http://bl.ocks.org/cpbotha/5200394 - shadow
// https://www.youtube.com/channel/UCHtLTNWrpDl_kwy3oOIiy9Q - react, d3 and typescript tutorial
// https://www.dashingd3js.com/table-of-contents - good tutorial
// https://www.netwoven.com/2018/01/16/different-ways-adding-custom-svg-images-d3-line-charts-angular-4/ - different ways of loading svg icons
// https://www.baeldung.com/spring-server-sent-events - server side send events


// progressing rendering ideas
// https://medium.com/@christopheviau/6-weird-tricks-for-insane-chart-performance-afd29f90f271
interface DrawProps {
    pathway: CohortPathway
    _selector: string
}

export const draw = ({ pathway, _selector }: DrawProps) => {
    // @ts-ignore
    // const data = x as EventMetric[]
    // const data = pathway.eventMetrics
    const data = pathway
        .eventConfigs
        .map((config, index) => {
            return {
                metric: pathway.eventMetrics[index],
                config: config
            }
        })

    const dataFiltered = _.takeWhile(data, function (o) { return !_.isNil(o.metric) })

    // cleanup
    d3
        .select(_selector + ' > *')
        .remove();

    let svg = d3
        .select(_selector)
        .append('svg')

    // for exports
    svg
        .append("rect")
        .attr("width", "100%")
        .attr("height", "100%")
        .attr("fill", "white");

    // https://yoksel.github.io/svg-filters/#/presets/shadow
    const filter = svg
        .append("defs")
        .append("filter")
        .attr("id", "shadowfilter")
        .attr("x", "0")
        .attr("y", "0")
        .attr("width", "200%")
        .attr("height", "200%")

    filter
        .append("feOffset")
        .attr("result", "offOut")
        .attr("in", "SourceAlpha")
        .attr("dx", "20")
        .attr("dy", "20")

    filter
        .append("feGaussianBlur")
        .attr("result", "blurOut")
        .attr("in", "offOut")
        .attr("stdDeviation", "10")

    filter
        .append("feBlend")
        .attr("in", "SourceGraphic")
        .attr("in2", "blurOut")
        .attr("mode", "normal")

    svg.node()?.getBoundingClientRect()
    // what does viewbox do???
    // .attr('viewBox', '0 0 ' + viewBoxWidth + ' ' + viewBoxHeight);

    // https://www.d3-graph-gallery.com/graph/interactivity_zoom.html
    // https://coderwall.com/p/psogia/simplest-way-to-add-zoom-pan-on-d3-js
    // svg
    //     .call(
    //         // @ts-ignore
    //         d3.zoom().on("zoom", function () {
    //             console.log(d3.event.transform)
    //             svg.attr("transform", d3.event.transform)
    //         }))

    // http://bl.ocks.org/linssen/7352810
    // https://github.com/d3/d3-zoom
    // https://observablehq.com/@d3/programmatic-zoom
    // https://www.dashingd3js.com/lessons/d3-zoom-for-svg-lines-and-svg-paths-part-two
    // https://www.dashingd3js.com/lessons/d3-zoom-behavior-part-two

    // post index
    const postIndexGroup = svg
        .append("g")
    const indexConfig = pathwayTheme.index
    const indexEventWidth = indexConfig.width
    const paddingTop = pathwayTheme.event.header.padding.top
    const rectHeight = 20

    postIndexGroup
        .append("rect")
        .attr("x", indexEventWidth)
        .attr("y", 0)
        .attr("height", rectHeight)
        .attr("width", '100%')
        .attr("rx", 8)
        .attr("ry", 8)
        .attr("fill", "#9e005d")
        .attr("stroke-width", 1)
        .attr("stroke", "#e7ebf0")

    postIndexGroup
        .append("text")
        .text("Post Index")
        .attr("x", indexEventWidth + paddingTop * 2)
        .attr("y", rectHeight / 2 + 12 / 4)
        .attr("fill", "white")
        .attr("font-family", "sans-serif")
        .attr("font-size", `12px`)

    const eventsGroup = svg
        .append("g")
        .attr("transform", `translate(0, ${25})`)

    // legend
    drawLegend(
        eventsGroup
            .append("g")
            .attr("transform", `translate(${indexConfig.legend.x}, ${paddingTop})`)
    )

    drawEvents(eventsGroup, dataFiltered)

    // filter descriptions
    const allFilters = dataFiltered
        .flatMap(i => {
            const filters: (EventFilter | DestinationFilter)[] = [...i.config.destinationFilters.filter(i => !filterIsEmpty(i.filter))]
            if (!_.isNil(i.config.eventFilter)) {
                if (!filterIsEmpty(i.config.eventFilter.filter)) {
                    filters.unshift(i.config.eventFilter)
                }
            }
            return filters
        })
    // const eachRowHeight = 29
    // const tableHeight = eachRowHeight + allFilters.length * eachRowHeight + 5
    const filtersTableFOId = "filters-table-fo"
    const filtersTableId = "filters-table"
    const table = postIndexGroup
        .append("g")
        .append("foreignObject")
        .attr("id", filtersTableFOId)
        .attr("y", 780)
        .attr("width", "100%") // TODO: find a better value
        .attr("height", "100%") // temporary, updated further down
        .append("xhtml:table")
        .attr("id", filtersTableId)
        .attr("class", "filters-table")

    table
        .append("xhtml:thead")
        .html("<tr><th>Label</th><th>Event</th><th>Destination(s)</th><th>Description</th></tr>")

    table
        .selectAll()
        .data(allFilters)
        .enter()
        .append("xhtml:tr")
        .attr("id", (d, i) => "filter-" + d.eventNo)
        .html((d, i) => {
            const label = "<td></td>"
            const event = `<td>${d?.eventNo}</td>`
            const destinations = ("datasets" in d) ? `<td>${d.datasets.map(i => getDatasetTheme(i).title).join(", ")}</td>` : `<td>${getDatasetTheme(d.dataset).title}</td>`
            const description = `<td>${("datasets" in d) ? eventFilterDescription(d) : destinationFilterDescription(d)}</td>`
            return `<tr>${label}${event}${destinations}${description}</tr>`
        })

    // last - set the dimensions of the svg
    setSvgDimensions(svg)

    // @ts-ignore
    const rect = d3.select(`#${filtersTableId}`).node().getBoundingClientRect()
    d3.select(`#${filtersTableId}`).attr("height", rect.height)
    d3.select(`#${filtersTableFOId}`).attr("height", rect.height)

    // update the dimensions of the svg based on the adjusted size of the filters table
    setSvgDimensions(svg)

    // // render html elements
    // const displayEventsId = 'display-events'
    // svg
    //     .append("foreignObject")
    //     .attr('x', 0)
    //     .attr('y', 0)
    //     .attr('width', 200)
    //     .attr('height', 100)
    //     .append('xhtml:div')
    //     .attr('id', displayEventsId)
    // render(DisplayEvents({}), document.getElementById(displayEventsId))
}

function setSvgDimensions(svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>) {
    svg
        .attr("width", (d, i, n) => {
            const rect = n[i].getBBox()
            return rect.width + 100 // some padding
        })
        .attr("height", (d, i, n) => {
            const rect = n[i].getBBox()
            return rect.height + 20 // some padding
        })
}

function drawEvents(svg: d3.Selection<SVGGElement, unknown, HTMLElement, any>, data: EventMetricWithConfig[]) {
    const indexEventWidth = pathwayTheme.index.width
    const eventWidth = pathwayTheme.event.width

    // draw index event
    drawIndexEvent(svg, data[0])

    // draw remaining events
    const eachEventSelection = svg
        .selectAll()
        // remove index event
        .data(_.tail(data))
        .enter()
        .append("g")
        .attr("transform", (d, index) => `translate(${indexEventWidth + (index * eventWidth)}, 0)`)

    // event divider
    const dividerHeight = pathwayTheme.event.dividerHeight
    eachEventSelection
        .append("line")
        .attr("stroke", "#000000")
        .attr("x1", eventWidth)
        .attr("y1", 0)
        .attr("x2", eventWidth)
        .attr("y2", dividerHeight)
        .attr("stroke-width", 1)

    // http://svghelper.com/
    // https://css-tricks.com/tools-visualize-edit-svg-paths-kinda/
    const config = pathwayTheme.event.header
    const contentHeight = pathwayTheme.metric.content.height(metricConfig[2].length)
    const magicNumber = 180

    const eachDestinationSelection = eachEventSelection
        .selectAll()
        .data((d, i) =>
            d
                .metric
                .metrics
                .map(metric => {
                    return {
                        metric: metric,
                        config: d.config
                    }
                }))
        .enter()

    const dividerOffsetY = config.height + config.padding.top + config.metrics.height
    const middleOfDivider = dividerOffsetY + ((dividerHeight - dividerOffsetY) / 2)
    // curve from divider to event
    eachDestinationSelection
        .append("path")
        .attr("d", (d, i) => {
            const start = { x: 0, y: middleOfDivider }
            const y = config.padding.top + config.height + config.metrics.height + d.metric.displayOrder * magicNumber + pathwayTheme.metric.actions.include.icon.y(contentHeight)
            const end = { x: pathwayTheme.metric.x, y: y }
            if (d.metric.dataset === 1 || d.metric.dataset === 2) {
                return `M${start.x},${start.y} C${end.x - 30},${start.y} ${start.x - 20},${end.y} ${end.x},${end.y}`
            } else {
                return `M${start.x},${start.y} C${end.x},${start.y} ${start.x},${end.y} ${end.x},${end.y}`
            }

        })
        .attr("stroke", (d, i) => getDatasetTheme(d.metric.dataset).colour)
        .attr("fill", "transparent")

    // curve from event to divider
    eachDestinationSelection
        .append("path")
        .attr("d", (d, i) => {
            const y = config.padding.top + config.height + config.metrics.height + d.metric.displayOrder * magicNumber + pathwayTheme.metric.actions.include.icon.y(contentHeight)
            const start = { x: pathwayTheme.metric.x + pathwayTheme.metric.content.width, y: y }

            const end = { x: pathwayTheme.event.width, y: middleOfDivider }
            if (d.metric.dataset === 1 || d.metric.dataset === 2) {
                return `M${start.x},${start.y} C${end.x + 30},${start.y} ${start.x + 20},${end.y} ${end.x},${end.y}`
            } else {
                return `M${start.x},${start.y} C${end.x},${start.y} ${start.x},${end.y} ${end.x},${end.y}`
            }
        })
        .style("stroke", (d, i) => {
            if (hasData(d.metric)) {
                return getDatasetTheme(d.metric.dataset).colour
            } else {
                return getNoDataTheme().colour
            }
        })
        .attr("fill", "transparent")

    drawEventHeader(eachEventSelection)

    const eachDestinationBoxSelection = eachDestinationSelection
        .append("g")

    // each box within each event
    metricBox(eachDestinationBoxSelection)

    const parentRect = svg.node()!.getBoundingClientRect()
    const offset = parentRect.y
    eachDestinationBoxSelection
        .attr("transform", (d, i, n) => {
            const previousSibling = (n[i].previousSibling as SVGTSpanElement)
            const rect = previousSibling.getBoundingClientRect()
            const y = (rect.bottom - offset) + 20
            return `translate(${pathwayTheme.metric.x}, ${y})`
        })
        .attr("id", (d, i) => `e${d.metric.eventNo}-d${d.metric.dataset}`)

    // median event days on each curve
    const medianDaysGroup =
        eachDestinationBoxSelection
            .filter((d, i) => hasData(d.metric))
            .append("g")

    const fontSize = 10
    const boxHeight = 15
    const boxWidth = 50

    medianDaysGroup
        .append("rect")
        .attr("height", boxHeight)
        .attr("width", boxWidth)
        .attr("rx", 8)
        .attr("ry", 8)
        .attr("fill", "white")
        .attr("stroke-width", 1)
        .attr("stroke", "#e7ebf0")

    medianDaysGroup
        .append("text")
        .text((d, i) => `${(d.metric as DestinationMetric).medianEventDays!} days`)
        .attr("fill", "black")
        .attr("font-family", config.font.fontFamily)
        .attr("font-size", `${fontSize}px`)
        .attr("y", boxHeight / 2 + fontSize * .3)
        .attr("x", (d, i, n) => {
            const rect = n[i].getBBox()
            return boxWidth / 2 - rect.width / 2
        })

    const headerHeight = pathwayTheme.metric.header.height
    medianDaysGroup
        .attr("transform", (d, i, n) => {
            const rect = n[i].getBBox()
            return `translate(${-rect.width}, ${headerHeight + 15})`
        })

    svg
        .selectAll("*[visibility=hidden]")
        .remove()
}

function drawIndexEvent(svg: d3.Selection<SVGGElement, unknown, HTMLElement, any>, indexEvent: EventMetricWithConfig) {
    const eventWidth = pathwayTheme.index.width
    const metricBoxWidth = pathwayTheme.metric.header.width
    const headerHeight = pathwayTheme.metric.header.height
    const fontSize = pathwayTheme.metric.content.font.size

    const metrics = indexEvent.metric.metrics
    const indexMetrics = _.filter(metrics, i => i.dataset < 0)
        .map((d, i) => {
            return {
                metric: d,
                config: indexEvent.config
            }
        })
    const exceptIndexMetrics = _.filter(metrics, i => i.dataset > 0)
        .map((d, i) => {
            return {
                metric: d,
                config: indexEvent.config
            }
        })

    const noOfMetricsInIndexDataset = !_.isNil(exceptIndexMetrics) && exceptIndexMetrics.length > 0 ? metricConfig[exceptIndexMetrics[0].metric.dataset].length : 0
    const eachDatasetHeight = 22 + pathwayTheme.metric.content.height(noOfMetricsInIndexDataset)
    const contentHeight = 10 + eachDatasetHeight * exceptIndexMetrics.length

    // index event divider
    svg
        .append("line")
        .attr("stroke", "#000000")
        .attr("x1", eventWidth)
        .attr("y1", 0)
        .attr("x2", eventWidth)
        .attr("y2", pathwayTheme.event.dividerHeight)
        .attr("stroke-width", 1)

    const group = svg
        .selectAll()
        .data(indexMetrics)
        .enter()
        .append("g")
        .attr("transform", `translate(${pathwayTheme.index.x}, ${260})`)

    // header
    drawMetricBoxHeader(group)

    // content
    group
        .append("path")
        .attr("d", bottomRoundedRect(0, headerHeight, metricBoxWidth, contentHeight, 4))
        .style("fill", "white")
        .attr("stroke-width", 1)
        .attr("stroke", "#e7ebf0")

    const padding = pathwayTheme.metric.padding.left
    const heightOfEachLine = pathwayTheme.metric.content.height(1)

    const eachDestinationSelection = group
        .selectAll()
        .data(exceptIndexMetrics)
        .enter()
        .append("g")
        .attr("transform", (d, index) => `translate(${0}, ${headerHeight + index * eachDatasetHeight})`)

    // title for each dataset
    drawMetricBoxHeaderText(eachDestinationSelection, "black")

    // metrics for each dataset
    const eachMetricLineSelection = eachDestinationSelection
        .filter((d, i) => hasData(d.metric))
        .selectAll()
        .data((d, i) =>
            metricConfig[d.metric.dataset]
                .map(i => {
                    return {
                        metric: d.metric as DestinationMetric,
                        config: i
                    }
                }))
        .enter()
        .append("g")
        .attr("transform", (d, index) => `translate(${padding}, ${22 + fontSize / 2 + index * heightOfEachLine})`)

    drawMetrics(eachMetricLineSelection)
}

function drawEventHeader(eachEventSelection: d3.Selection<SVGGElement, EventMetricWithConfig, SVGGElement, unknown>) {
    const config = pathwayTheme.event.header

    // actions before and after header
    eachEventSelection
        .each(function (d, index, items) {
            const size = pathwayTheme.metric.actions.include.icon.size

            const minus = (d3.select(this) as d3.Selection<SVGGElement, any, null, undefined>)
                .append("g")
                .attr("class", "event-minus")
                .on("click", (d, i) => {
                    store.dispatch(decrementEvents())
                })
            pathwayOnIcon(minus, { size: size, x: 25, y: 8 }, "#e7ebf0", "#98a4b1")


            if ((index + 1) !== MAX_EVENT_NO) {
                const plus = (d3.select(this) as d3.Selection<SVGGElement, any, null, undefined>)
                    .append("g")
                    .attr("class", "event-plus")
                    .on("click", (d, i) => {
                        store.dispatch(incrementEvents())
                    })
                pathwayOffIcon(plus, { size: size, x: config.width + 50, y: 8 }, "#e7ebf0", "#98a4b1")
            }
        })

    // event header
    const headerTitle = eachEventSelection
        .append("g")
        .attr("transform", (d, eventNo) => `translate(${config.x}, 0)`)

    // event labels
    headerTitle
        .append("rect")
        .attr("y", config.padding.top)
        .attr("height", config.height)
        .attr("width", config.width)
        .attr("rx", 8)
        .attr("ry", 8)
        .attr("fill", (d, i) => {
            if (_.isNil(d.config.eventFilter) || filterIsEmpty(d.config.eventFilter.filter)) {
                return "white"
            } else {
                return "#00aafb"
            }
        })
        .attr("stroke-width", 1)
        .attr("stroke", "#e7ebf0")
        .attr("class", "event-header")
        .on("click", (d, i) => {
            store.dispatch(filterOnEvent(d.config))
        })

    headerTitle
        .append("text")
        .attr("fill", "black")
        .attr("font-family", config.font.fontFamily)
        .attr("font-size", `${config.font.size}px`)
        .text((d, i) => `Event ${d.config.eventNo}`)
        .attr("x", (d, i, n) => {
            const textRect = n[i].getBBox()
            return centre(config.width, textRect.width)
        })
        .attr("y", config.height * 1.2)

    // event header metrics
    const headerMetricsGroup = eachEventSelection
        .append("g")
        .attr("id", (d) => `header-metrics-event-${d.config.eventNo}`)
        .attr("transform", (d) => `translate(${config.x}, ${config.height + config.padding.top})`)

    headerMetricsGroup
        .append("rect")
        .attr("y", 10)
        .attr("height", config.metrics.height)
        .attr("width", config.metrics.width)
        .attr("rx", 4)
        .attr("ry", 4)
        .attr("fill", "#fdfdfd")
        .attr("stroke-width", 1)
        .attr("stroke", "#efefef")

    const eachMetricWidth = config.metrics.width / 3
    const calcXY = (row: number, col: number) => {
        return {
            x: col * eachMetricWidth,
            y: 25 + (row === 0 ? 0 : config.metrics.font.size + config.metrics.subFont.size * --row)
        }
    }

    const eachHeaderMetricsSelection = headerMetricsGroup
        .selectAll()
        .data((d, i) => [
            [
                {
                    ...calcXY(0, 0),
                    text: d.metric.totalPatients.toLocaleString(),
                    suppressed: true
                },
                {
                    ...calcXY(1, 0),
                    text: "Total Patient"
                },
                {
                    ...calcXY(2, 0),
                    text: "Count"
                }
            ],
            [
                {
                    ...calcXY(0, 1),
                    text: d.metric.lostPatients.toLocaleString(),
                    suppressed: true
                },
                {
                    ...calcXY(1, 1),
                    text: "Total Patients"
                },
                {
                    ...calcXY(2, 1),
                    text: "Lost"
                }
            ],
            [
                {
                    ...calcXY(0, 2),
                    text: `${d.metric.lostPatientsPercent.toLocaleString()}%`
                },
                {
                    ...calcXY(1, 2),
                    text: "% of Patients"
                },
                {
                    ...calcXY(2, 2),
                    text: "Lost"
                }
            ]
        ])
        .enter()

    eachHeaderMetricsSelection
        .append("text")
        .selectAll()
        .data((d, i) => d)
        .enter()
        .append("tspan")
        // @ts-ignore
        .attr("data-testid", (d, i) => d.suppressed ? "suppressed" : null)
        .attr("fill", (d, i) => i === 0 ? config.metrics.font.colour : config.metrics.subFont.colour)
        .attr("font-family", (d, i) => i === 0 ? config.metrics.font.fontFamily : config.metrics.subFont.fontFamily)
        .attr("font-size", (d, i) => i === 0 ? `${config.metrics.font.size}px` : `${config.metrics.subFont.size}px`)
        .text((d, i) => d.text)
        .attr("x", (d, i, n) => d.x + centre(eachMetricWidth, n[i].getBBox().width))
        .attr("y", (d, i, n) => d.y)

    eachHeaderMetricsSelection
        .append("line")
        .attr("stroke", "#f2f2f2")
        .attr("x1", (d, i) => i * eachMetricWidth)
        .attr("y1", 15)
        .attr("x2", (d, i) => i * eachMetricWidth)
        .attr("y2", 45)
        .attr("stroke-width", 1)
        .attr("visibility", (d, i) => {
            // hide the first divider
            return i === 0 ? "hidden" : ""
        })
}
